/**
 * Async job polling for free-tool AI calls.
 *
 * Free tool endpoints (geo-checkup, competitor-radar, report-analytics) now
 * return {job_id, status:"pending"} immediately (bypassing Cloudflare's 100s
 * origin timeout) and run the LLM in a BackgroundTask. Poll /tools/jobs/{id}
 * every 3s until status is "done" or "error".
 *
 * Usage:
 *   const result = await runToolJob(`${API}/tools/geo-checkup`, payload, authHeaders);
 */
import axios from 'axios';

const POLL_INTERVAL_MS = 3000;
const MAX_POLLS = 60; // 60 * 3s = 3 minutes hard cap

export async function runToolJob(postUrl, body, headers) {
  const { data: created } = await axios.post(postUrl, body, { headers });
  if (created?.result) return created.result; // legacy path (sync response)
  if (!created?.job_id) throw new Error('No job_id in response');

  const jobsBase = postUrl.replace(/\/tools\/[^/]+$/, '/tools/jobs');
  const jobUrl = `${jobsBase}/${created.job_id}`;

  for (let i = 0; i < MAX_POLLS; i++) {
    await new Promise((res) => setTimeout(res, POLL_INTERVAL_MS));
    const { data: status } = await axios.get(jobUrl, { headers });
    if (status.status === 'done') return status.result;
    if (status.status === 'error') {
      const err = new Error(status.detail || 'AI job failed');
      err.response = { data: { detail: status.detail }, status: status.http_status || 502 };
      throw err;
    }
  }
  throw new Error('Job took too long — please try again');
}
